import 'core-js/modules/es.object.assign.js';
import { __awaiter } from '../../../_virtual/_tslib.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Modal, Stack, Text, Tooltip, TextField, Button, Snackbar } from '@hub-la/design-system';
import { useGetBusiness, DocumentType } from '@hub-la/fe-get-user';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFormik, FormikProvider } from 'formik';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import * as Yup from 'yup';
import { BankAccountType } from '../../domain/enums/bank-account.js';
import { WithdrawalForm } from '../components/withdrawal-form.js';
import { useUpdateAccount } from '../hooks/use-update-account.js';
import { bankAccountSchema } from '../validation/bank-account-schema.js';

const UpdateAccountModal = props => {
  var _a, _b, _c, _d, _e, _f, _g;
  const {
    open,
    onClose,
    onUpdate
  } = props;
  const {
    t
  } = useTranslation();
  const {
    data: account
  } = useGetBusiness();
  const {
    mutateAsync: updateAccount,
    isLoading,
    error,
    reset,
    isSuccess
  } = useUpdateAccount();
  const isCnpj = ((_a = account === null || account === void 0 ? void 0 : account.businessOwner) === null || _a === void 0 ? void 0 : _a.documentType) === DocumentType.CNPJ;
  const accountType = isCnpj ? 'cnpj' : 'cpf';
  const formik = useFormik({
    validationSchema: Yup.object().shape({
      isPixKey: Yup.boolean(),
      bankAccount: Yup.mixed().when('isPixKey', {
        is: false,
        then: bankAccountSchema.fields.bankAccount
      })
    }),
    validateOnMount: true,
    validateOnChange: true,
    enableReinitialize: true,
    initialValues: {
      bankAccount: {
        bankNumber: '001',
        agencyNumber: '',
        agencyCheckNumber: '',
        accountNumber: '',
        accountCheckNumber: '',
        accountType: BankAccountType.TYPE_CHECKING
      },
      isPixKey: (_c = (_b = account === null || account === void 0 ? void 0 : account.bankAccount) === null || _b === void 0 ? void 0 : _b.isPixKey) !== null && _c !== void 0 ? _c : false
    },
    onSubmit: values => __awaiter(void 0, void 0, void 0, function* () {
      updateAccount(values).then(() => {
        onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate();
      });
    })
  });
  return jsxs(Fragment, {
    children: [jsx(Modal, Object.assign({
      size: "full",
      open: open,
      onClose: onClose
    }, {
      children: jsx(FormikProvider, Object.assign({
        value: formik
      }, {
        children: jsxs(Stack, Object.assign({
          direction: "column",
          spacing: 6
        }, {
          children: [jsxs(Stack, Object.assign({
            direction: "row",
            onClick: onClose,
            alignSelf: "end",
            style: {
              cursor: 'pointer'
            }
          }, {
            children: [jsx(Text, Object.assign({
              variant: "body1"
            }, {
              children: t('accountVerification.form.close')
            })), jsx(CloseOutlinedIcon, {})]
          })), jsx(Text, Object.assign({
            variant: "h3"
          }, {
            children: t('updateAccount.title')
          })), jsxs(Stack, Object.assign({
            direction: "row",
            spacing: 1
          }, {
            children: [jsx(Text, {
              children: t(`updateAccount.${accountType}.label`)
            }), jsx(Tooltip, Object.assign({
              trigger: "hover",
              title: jsx(Text, {
                children: t(`updateAccount.${accountType}.tooltip`)
              }),
              arrow: true
            }, {
              children: jsx(InfoOutlinedIcon, {})
            }))]
          })), ((_d = account === null || account === void 0 ? void 0 : account.businessOwner) === null || _d === void 0 ? void 0 : _d.fullName) && jsx(TextField, {
            value: account === null || account === void 0 ? void 0 : account.businessOwner.fullName,
            size: "normal",
            label: t('fullName'),
            disabled: true
          }), ((_e = account === null || account === void 0 ? void 0 : account.businessOwner) === null || _e === void 0 ? void 0 : _e.documentNumber) && jsx(NumberFormat, {
            value: account === null || account === void 0 ? void 0 : account.businessOwner.documentNumber,
            type: "tel" // numbers only
            ,
            size: "normal",
            label: t(`documentTypeEnum.${(_f = account === null || account === void 0 ? void 0 : account.businessOwner) === null || _f === void 0 ? void 0 : _f.documentType}`),
            format: isCnpj ? '##.###.###/####-##' : '###.###.###-##',
            customInput: TextField,
            disabled: true,
            fullWidth: true
          }), jsx(WithdrawalForm, {}), jsx(Stack, Object.assign({
            alignSelf: "end"
          }, {
            children: jsx(Button, Object.assign({
              variant: "filled",
              disabled: !formik.isValid,
              loading: isLoading,
              onClick: () => formik.submitForm()
            }, {
              children: t('updateAccount.submit')
            }))
          }))]
        }))
      }))
    })), jsx(Snackbar, Object.assign({
      open: !!error,
      variant: "negative",
      onClose: () => reset(),
      autoHideDuration: null
    }, {
      children: (_g = t(error === null || error === void 0 ? void 0 : error.message)) !== null && _g !== void 0 ? _g : ''
    })), jsx(Snackbar, Object.assign({
      open: isSuccess,
      variant: "positive",
      closeable: false,
      onClose: () => reset()
    }, {
      children: t('success')
    }))]
  });
};

export { UpdateAccountModal };
